import { Editor as EditorRef } from "@tinymce/tinymce-react";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SSMLTag, SSMLTagsColors, pitchOptions } from "shared/constants";
import { presetPitchValues } from "shared/models";
import { handleSSMLValueClick } from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import {
  getPresetPitchValue,
  getSignedPitchValue,
} from "shared/utils/tts-custom-editor";
import MenuWrapper from "../../components/MenuWrapper";
import CustomDropdown from "../../components/PresetCustomMenu/CustomDropdown";

interface PitchMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  onPitchChange?: (value: string) => void;
  onApply?: (value: string) => void;
}

export default function PitchMenu({
  editorRef,
  onPitchChange,
  onApply,
}: PitchMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [pitchUnit, setPitchUnit] = useState<string>(pitchOptions[0]);
  const [pendingPitchValue, setPendingPitchValue] = useState<string | null>(
    null
  );
  const [initialValue, setInitialValue] = useState<string>("default (0st)");
  const [activeValue, setActiveValue] = useState<string>("");
  const [isCustomValueActive, setIsCustomValueActive] =
    useState<boolean>(false);
  const [customInputValue, setCustomInputValue] = useState<string>("");

  useEffect(() => {
    if (editorRef.current?.editor) {
      const currentPitchValue = getCurrentSSMLValue(editorRef, SSMLTag.Pitch);

      if (currentPitchValue) {
        const presetMatch = presetPitchValues.find(
          (preset) =>
            preset.value === currentPitchValue ||
            preset.label === currentPitchValue
        );

        if (presetMatch) {
          const displayText = `${presetMatch.label} (${presetMatch.value})`;
          setInitialValue(displayText);
          setPendingPitchValue(presetMatch.value);
          setActiveValue(presetMatch.value);
          setIsCustomValueActive(false);
          setCustomInputValue("");
        } else {
          const unitMatch = currentPitchValue.match(
            /([+-]?\d+(?:\.\d+)?)(\w+|%)/
          );
          if (unitMatch) {
            const [, numericValue, unit] = unitMatch;
            setInitialValue(`Custom (${numericValue}${unit})`);
            setPendingPitchValue(numericValue);
            setPitchUnit(unit);
            setActiveValue(`${numericValue}${unit}`);
            setIsCustomValueActive(true);
            const numericOnly = numericValue.replace(/^\+/, "");
            setCustomInputValue(numericOnly);
          } else {
            setInitialValue(`Custom (${currentPitchValue})`);
            setPendingPitchValue(currentPitchValue);
            setActiveValue(currentPitchValue);
            setIsCustomValueActive(true);
            setCustomInputValue(currentPitchValue);
          }
        }
        if (onPitchChange) {
          onPitchChange(currentPitchValue);
        }
      } else {
        setInitialValue("default (0st)");
        setPendingPitchValue(null);
        setPitchUnit(pitchOptions[0]);
        setActiveValue("0st");
        setIsCustomValueActive(false);
        setCustomInputValue("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef]);

  const handlePitchValueSelect = (pitchValue: string) => {
    if (pitchValue.trim() === "") return;
    setPendingPitchValue(pitchValue);

    // Call onPitchChange with pending value for preview/UI updates
    if (onPitchChange) {
      const presetPitchContent = getPresetPitchValue(pitchValue);
      const pitch =
        presetPitchContent?.label ||
        `${getSignedPitchValue(pitchValue)}${pitchUnit}`;
      onPitchChange(pitch);
    }
  };

  const applyPitch = () => {
    if (!pendingPitchValue || !editorRef.current) return;

    const presetPitchContent = getPresetPitchValue(pendingPitchValue);
    const pitch =
      presetPitchContent?.label ||
      `${getSignedPitchValue(pendingPitchValue)}${pitchUnit}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.Pitch,
      pitch,
      SSMLTagsColors.ProsodyTag,
      []
    );
    // Call onApply if provided
    if (onApply) {
      onApply(pitch);
    }
  };

  return (
    <MenuWrapper
      id="pitch-menu-wrapper"
      onApply={applyPitch}
      showDeleteButton={false}
      editorRef={editorRef}
      tagType={SSMLTag.Pitch}
    >
      <CustomDropdown
        key={initialValue}
        showCustomTextField
        showPresetMenuList
        presetHeader={t("tts.toolbarMenu.pitchMenu.presetPitchHeader")}
        presetValues={presetPitchValues}
        customHeader={t("tts.toolbarMenu.pitchMenu.customPitchHeader")}
        handleClick={handlePitchValueSelect}
        allowNegativeCustomValue
        unitOptions={pitchOptions}
        selectedUnit={pitchUnit}
        setSelectedUnit={setPitchUnit}
        defaultValue={initialValue}
        activeValue={activeValue}
        isCustomValueActive={isCustomValueActive}
        value={customInputValue}
      />
    </MenuWrapper>
  );
}
